import { createContext, useContext, useEffect, useState } from "react";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { useTranslation } from "../hooks/useTranslation";
import { isLikelyFrench, translatePreservingFrench } from "../utils/lang";

const ContentContext = createContext();

export const ContentProvider = ({ children }) => {
  // Original Firestore data (English or mixed)
  const [originalCategories, setOriginalCategories] = useState([]);
  const [originalSeries, setOriginalSeries] = useState([]);
  const [originalLiveEpisodes, setOriginalLiveEpisodes] = useState([]);

  // Translated data for display
  const [categories, setCategories] = useState([]);
  const [series, setSeries] = useState([]);
  const [liveEpisodes, setLiveEpisodes] = useState([]);

  const [loading, setLoading] = useState(true);
  const [translating, setTranslating] = useState(false);

  const { currentLanguage, translateText } = useTranslation();

  // Translation cache
  const [translationCache, setTranslationCache] = useState(new Map());
  // hydrate cache from localStorage (persisted across sessions)
  useEffect(() => {
    try {
      const raw = localStorage.getItem("translationCache_v1");
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === "object") {
          setTranslationCache(new Map(Object.entries(obj)));
        }
      }
    } catch (e) {
      console.warn("Failed to read translation cache:", e);
    }
  }, []);
  const TRANSLATION_CACHE_VERSION = "1";

  const getCachedTranslation = async (text, targetLang) => {
    if (!text || targetLang === "en") return text;
    const normalizedTarget = (targetLang || "").toLowerCase();
    const cacheKey = `${TRANSLATION_CACHE_VERSION}|${normalizedTarget}|${text}`;
    if (translationCache.has(cacheKey)) {
      return translationCache.get(cacheKey);
    }
    try {
      const out =
        normalizedTarget === "fr"
          ? await translatePreservingFrench(text, "fr", translateText)
          : await translateText(text, normalizedTarget);
      setTranslationCache((prev) => {
        const next = new Map(prev);
        next.set(cacheKey, out);
        try {
          const obj = Object.fromEntries(next);
          localStorage.setItem("translationCache_v1", JSON.stringify(obj));
        } catch (e) {
          console.warn("Failed to persist translation cache:", e);
        }
        return next;
      });
      return out;
    } catch (e) {
      console.error("Translation error:", e);
      return text;
    }
  };

  useEffect(() => {
    // Categories listener
    const unsubscribeCategories = onSnapshot(
      doc(db, "content", "main"),
      (docSnap) => {
        setOriginalCategories(docSnap.data()?.categories || []);
      },
    );

    // Series collection listener
    const seriesRef = collection(db, "content", "main", "series");
    const unsubscribeSeries = onSnapshot(seriesRef, (seriesSnapshot) => {
      const seriesData = seriesSnapshot.docs.map((seriesDoc) => ({
        id: seriesDoc.id,
        ...seriesDoc.data(),
      }));
      setOriginalSeries(seriesData);
      setLoading(false);
    });

    // Live episodes listener
    const liveEpisodesQuery = query(
      collection(db, "episodes"),
      where("isLive", "==", true),
    );

    const unsubscribeLiveEpisodes = onSnapshot(
      liveEpisodesQuery,
      (snapshot) => {
        const liveEpisodesData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setOriginalLiveEpisodes(liveEpisodesData);
      },
    );

    return () => {
      unsubscribeCategories();
      unsubscribeSeries();
      unsubscribeLiveEpisodes();
    };
  }, []);

  // Translation helpers
  const translateCategories = async (arr) => {
    if (currentLanguage === "en") return arr;
    return Promise.all(
      (arr || []).map(async (c) => ({
        ...c,
        name: await getCachedTranslation(c.name, currentLanguage),
        description: c.description
          ? await getCachedTranslation(c.description, currentLanguage)
          : c.description,
      })),
    );
  };

  const translateSeries = async (arr) => {
    if (currentLanguage === "en") return arr;
    return Promise.all(
      (arr || []).map(async (s) => ({
        ...s,
        title: await getCachedTranslation(s.title, currentLanguage),
        description: s.description
          ? await getCachedTranslation(s.description, currentLanguage)
          : s.description,
        episodes: s.episodes
          ? await Promise.all(
              s.episodes.map(async (ep) => ({
                ...ep,
                title: await getCachedTranslation(ep.title, currentLanguage),
                description: ep.description
                  ? await getCachedTranslation(ep.description, currentLanguage)
                  : ep.description,
              })),
            )
          : s.episodes,
      })),
    );
  };

  const translateLiveEpisodes = async (arr) => {
    if (currentLanguage === "en") return arr;
    return Promise.all(
      (arr || []).map(async (ep) => ({
        ...ep,
        title: await getCachedTranslation(ep.title, currentLanguage),
        description: ep.description
          ? await getCachedTranslation(ep.description, currentLanguage)
          : ep.description,
      })),
    );
  };

  // Recompute translated data when language or originals change
  useEffect(() => {
    const doTranslate = async () => {
      if (loading) return;
      setTranslating(true);
      try {
        const [tc, ts, tle] = await Promise.all([
          translateCategories(originalCategories),
          translateSeries(originalSeries),
          translateLiveEpisodes(originalLiveEpisodes),
        ]);
        setCategories(tc);
        setSeries(ts);
        setLiveEpisodes(tle);
      } catch (e) {
        console.error("Translation pipeline error:", e);
        setCategories(originalCategories);
        setSeries(originalSeries);
        setLiveEpisodes(originalLiveEpisodes);
      } finally {
        setTranslating(false);
      }
    };
    doTranslate();
  }, [
    currentLanguage,
    originalCategories,
    originalSeries,
    originalLiveEpisodes,
    loading,
  ]);

  const contextValue = {
    categories,
    series,
    liveEpisodes,
    loading: loading || translating,
    isTranslating: translating,
    currentLanguage,
    originalData: {
      categories: originalCategories,
      series: originalSeries,
      liveEpisodes: originalLiveEpisodes,
    },
  };

  return (
    <ContentContext.Provider value={contextValue}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => useContext(ContentContext);
